// Externals
import type { Store } from 'redux';
import isEmpty from 'lodash.isempty';

// Libraries
import { BaseMiddleware, navigateTo } from '@makemydeal/dr-activities-common';
import { Actions, Actions as Action } from '../actionTypes/actionTypes';
import { showGlobalSpinner } from '../globalSpinner/globalSpinnerActions';
import type { Selectors } from './rootSelectors';
import { getSalesCheckInMode } from './selector';
import {
    CLEAR_ROLE_ASSUMPTION,
    extractParams,
    INITIALIZING_ROLE_ASSUMPTION,
    ROLE_ASSUMPTION_ROUTE,
    ROLE_ASSUMTION,
    SAVE_ROLE_ASSUMPTED_USERNAME,
    START_ROLE_ASSUMPTION
} from '@makemydeal/dr-activities-role-assumption';
import { FEATURES, routingConfig, salesCheckInMode, WorkflowStatus } from '@makemydeal/dr-sales-checkin-types';

// Types
import type { IConfigSalesCheckin, IHostActions } from '@makemydeal/dr-sales-checkin-types';

// Actions
import * as salesCheckInAction from '../deepLink/deepLinkActionMethods';
import * as deals from '../deals/dealsActionMethods';
import { DealVersionChangeType } from '@makemydeal/dr-shared-bff-types';
import { getDealVisibility } from '../activeDeal/selector';
import { refreshDealRequested } from '../dealRefresh/dealRefreshActionCreators';
import { getShowroomWorkflowStatus } from '../workflow/selector';
import { isFeatureVisible } from '../capabilities/selector';
import { setWorkflowStatus } from '../workflow/workflowActionCreators';
import { mapApplicationStateToWorkflowStatus } from '../workflow/applicationStateMap';
import { isFullPageScrollEnabled } from '../showroomNav/selector';
import { activateSection } from '../showroomNav/showroomNavActions';
import type { ReduxStore } from '@makemydeal/dr-shared-store';
import { bootstrapActionTypes } from '@makemydeal/dr-shared-store';
import type { SalesCheckinRootState } from './types/SalesCheckinRootState';

export class MiddlewareManager extends BaseMiddleware {
    // Set on CLEAR_ROLE_ASSUMPTION ("Stop Viewing As"); cleared once the refetch it gates has
    // been dispatched on the next START_ROLE_ASSUMPTION. See the START_ROLE_ASSUMPTION case
    // below for why this refetch is needed.
    private hasStoppedRoleAssumption = false;

    constructor(
        public selectors: Selectors,
        public hostActions: IHostActions,
        public config: IConfigSalesCheckin,
        public baseRoute?: string,
        public analyticsAction?: (store: any, key: string, desc: string) => void
    ) {
        super(selectors, analyticsAction);
    }

    private resetDealApprovalStatus = (store: ReduxStore<SalesCheckinRootState>): void => {
        const state = store.getState();
        store.dispatch({ type: Action.SET_DEAL_ACCEPTED_BY_BUYER, payload: { acceptedByBuyer: false } });
        const visibility = getDealVisibility(state);
        if (visibility && !isEmpty(visibility)) {
            visibility.approvedByManager = false;
            visibility.isDraftDeal = false;
            visibility.needsManagerApproval = true;
            store.dispatch({ type: Action.UPDATE_ACTIVE_DEAL_VISIBILITY_STATUS, payload: { visibility } });
        }
    };

    protected middlewareFunction = (store: Store<any>, _next: any, action: any): void => {
        switch (action.type) {
            case Actions.UPDATE_DEAL: {
                const state = store.getState();
                // Forward meta so post-save intent (e.g. navigateOnSuccess) survives onto UPDATE_DEAL_SUCCESS.
                this.resetDealApprovalStatus(store);

                const isSalesViewRedirectionAllowed = isFeatureVisible(FEATURES.SALES_VIEW_REDIRECTION)(state);
                const isInvalidStatus = getShowroomWorkflowStatus(state) === WorkflowStatus.INVALID;

                // An explicit changeType on the action (e.g. DEAL_SETUP from Launch Deal) always wins;
                // otherwise fall back to the vehicle-change derivation used by e.g. Change Vehicle.
                const changeType =
                    action.payload?.changeType ??
                    (!isSalesViewRedirectionAllowed && isInvalidStatus ? undefined : DealVersionChangeType.VEHICLE);

                store.dispatch(deals.updateDeal(this.selectors, action.meta, changeType));
                break;
            }
            case Actions.SET_CO_BUYER:
            case Actions.REMOVE_CO_BUYER:
            case Actions.UPDATE_CURRENT_CUSTOMER: {
                this.resetDealApprovalStatus(store);
                store.dispatch(deals.updateDeal(this.selectors, undefined, DealVersionChangeType.CUSTOMER));
                break;
            }
            case Actions.UPDATE_DEAL_SUMMARY: {
                // Forward meta so post-save intent (e.g. sentToDesk) survives onto UPDATE_DEAL_SUCCESS.
                store.dispatch(deals.updateDeal(this.selectors, action.meta, DealVersionChangeType.REQUEST_PROPOSAL));
                break;
            }
            case Actions.DEALER_DETAILS: {
                store.dispatch(salesCheckInAction.getDealerDetailsData(this.selectors));
                break;
            }
            case bootstrapActionTypes.BOOTSTRAP_SUCCESS: {
                store.dispatch(salesCheckInAction.getDeepLinkUrls(this.selectors));
                break;
            }
            case Actions.UPDATE_DEAL_SUCCESS: {
                // The save was the user's commitment to move on, so honour the requested destination
                // now that the server has confirmed it. Nothing here is status-derived: the caller
                // named the route, which is why swapping a vehicle navigates even though the
                // workflow status never changes.
                const navigateOnSuccess = action.meta?.navigateOnSuccess;
                if (navigateOnSuccess) {
                    const state = store.getState();
                    if (isFullPageScrollEnabled(state)) {
                        // Under full-scroll there is no standalone screen at `navigateOnSuccess` to
                        // land on (e.g. adding/changing a vehicle from CVI names Vehicle Details) —
                        // scroll to the matching section on the one page instead (US2128205).
                        store.dispatch(activateSection('vehicleDetails'));
                        store.dispatch(navigateTo(routingConfig.BUILD_YOUR_OFFER));
                    } else {
                        store.dispatch(navigateTo(navigateOnSuccess));
                    }
                }
                break;
            }
            case SAVE_ROLE_ASSUMPTED_USERNAME: {
                sessionStorage.setItem(ROLE_ASSUMTION, action.userName);
                break;
            }
            case CLEAR_ROLE_ASSUMPTION: {
                const state = store.getState();
                const mode = getSalesCheckInMode(state);
                const queryParams = action.payload;
                const newQueryParams =
                    mode === salesCheckInMode.Standalone ? `?${extractParams(queryParams, ['uiEnv', 'bffEnv'])}` : queryParams;
                sessionStorage.removeItem(ROLE_ASSUMTION);
                store.dispatch(navigateTo(`${ROLE_ASSUMPTION_ROUTE}${newQueryParams}`));
                this.hasStoppedRoleAssumption = true;
                break;
            }
            case START_ROLE_ASSUMPTION: {
                if (this.hasStoppedRoleAssumption) {
                    this.hasStoppedRoleAssumption = false;
                    store.dispatch(refreshDealRequested());
                }
                break;
            }
            case INITIALIZING_ROLE_ASSUMPTION: {
                store.dispatch(showGlobalSpinner());
                break;
            }
            case Actions.TRADE_IN_UPDATE_SUCCESS:
            case Actions.TRADE_IN_REMOVE_SUCCESS: {
                const applicationState = action.payload.data?.applicationState;
                // Translate before dispatching: the server's "VALID" has no matching WorkflowStatus on our
                // side (our equivalent is REDIRECT_TO_SALES_VIEW). Dispatching the raw string means the later
                // route lookup finds nothing and silently skips navigation — the redirect just never happens.
                if (applicationState) store.dispatch(setWorkflowStatus(mapApplicationStateToWorkflowStatus(applicationState)));
                break;
            }

            case Actions.UPDATE_ACTIVE_DEAL_FROM_PARTICIPANTS_SAVE: {
                const applicationState = action.payload?.applicationState;
                // Translate before dispatching: the server's "VALID" has no matching WorkflowStatus on our
                // side (our equivalent is REDIRECT_TO_SALES_VIEW). Dispatching the raw string means the later
                // route lookup finds nothing and silently skips navigation — the redirect just never happens.
                if (applicationState) store.dispatch(setWorkflowStatus(mapApplicationStateToWorkflowStatus(applicationState)));
                break;
            }
        }
    };
}
